import React, { useRef } from 'react';
import {
  AriaPositionProps,
  mergeProps,
  OverlayContainer,
  useMenuTrigger,
  useOverlayPosition,
} from 'react-aria';
import { MenuTriggerProps, useMenuTriggerState } from 'react-stately';
import Item from './Item';
import LinkItem from './LinkItem';
import MenuHeader from './MenuHeader';
import Popover from './Popover';
import PopoverMenu, { PopoverMenuProps } from './PopoverMenu';
import * as S from './styles';

export interface MenuProps<T extends object = object>
  extends Omit<MenuTriggerProps, 'align' | 'direction' | 'shouldFlip'>,
    PopoverMenuProps<T>,
    Pick<AriaPositionProps, 'placement' | 'offset'> {
  /**
   * The trigger button for opening the Menu.
   *
   * This element should accept all `AriaButtonProps`.  Using any button from this
   * library will work as a trigger element.
   *
   * Note that any ref assigned to this trigger element will get overwritten by
   * the Menu component.
   */
  button: React.ReactElement;
}

function Menu<T extends object>({
  button,
  placement,
  offset,
  ...props
}: MenuProps<T>): React.ReactElement | null {
  const buttonRef = useRef<Element>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const state = useMenuTriggerState(props);
  const { menuTriggerProps, menuProps } = useMenuTrigger<T>(
    {},
    state,
    buttonRef,
  );
  const { overlayProps } = useOverlayPosition({
    offset,
    placement,
    isOpen: state.isOpen,
    targetRef: buttonRef,
    overlayRef: popoverRef,
  });

  return (
    <S.Root>
      {React.cloneElement(button, {
        ref: buttonRef,
        ...mergeProps(button.props, menuTriggerProps),
      })}
      {state.isOpen && (
        <OverlayContainer>
          <Popover
            isOpen={state.isOpen}
            onClose={state.close}
            ref={popoverRef}
            {...overlayProps}
          >
            <PopoverMenu {...menuProps} {...props} />
          </Popover>
        </OverlayContainer>
      )}
    </S.Root>
  );
}

Menu.Item = Item;
Menu.LinkItem = LinkItem;
Menu.Header = MenuHeader;

export default Menu;
